import React from 'react';
import PropTypes from 'prop-types';
import { LuBell, LuFileSpreadsheet, LuLoaderCircle, LuTriangleAlert, LuUserPlus, LuUsers } from 'react-icons/lu';
import Button from '../../../components/ui/Button';
import GlassIcon from '../../../components/ui/GlassIcon';
import IconButton from '../../../components/ui/IconButton';
import { useTranslation } from '../../../i18n/LanguageContext';

/** Title card of Çalışan Yönetimi with the page-level actions. */
const EmployeePageHeader = ({
  pendingLeaveCount,
  missingDocsCount,
  isExporting,
  onOpenPendingLeaves,
  onOpenMissingDocs,
  onDownload,
  onAddEmployee,
}) => {
  const { t } = useTranslation();

  return (
    <section className="tw-relative tw-isolate tw-overflow-hidden tw-rounded-[1.75rem] tw-border tw-border-white/80 tw-bg-white/70 tw-p-4 tw-shadow-[inset_0_1px_0_rgba(255,255,255,1),0_20px_50px_-30px_rgba(15,23,42,0.35)] sm:tw-p-6 [@media(max-height:820px)]:tw-py-4 dark:tw-border-white/10 dark:tw-bg-[#22262f]/80 dark:tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_20px_50px_-30px_rgba(0,0,0,0.9)]">
      <div aria-hidden="true" className="tw-pointer-events-none tw-absolute -tw-left-20 -tw-top-24 -tw-z-10 tw-h-60 tw-w-60 tw-rounded-full tw-bg-teal-300/40 tw-blur-3xl dark:tw-bg-teal-500/15" />
      <div aria-hidden="true" className="tw-pointer-events-none tw-absolute -tw-right-10 -tw-top-24 -tw-z-10 tw-h-56 tw-w-56 tw-rounded-full tw-bg-sky-300/30 tw-blur-3xl dark:tw-bg-sky-500/10" />

      <div className="tw-flex tw-flex-col tw-gap-4 sm:tw-gap-5 lg:tw-flex-row lg:tw-items-center lg:tw-justify-between">
        <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-3 sm:tw-gap-4">
          <GlassIcon gradient="tw-from-teal-400 tw-to-cyan-600 tw-shadow-teal-500/30" size="md" className="sm:tw-h-14 sm:tw-w-14 sm:tw-rounded-[18px]">
            <LuUsers aria-hidden="true" className="tw-h-6 tw-w-6 sm:tw-h-7 sm:tw-w-7" />
          </GlassIcon>
          <div className="tw-min-w-0">
            <h1 className="tw-truncate tw-text-xl tw-font-semibold tw-tracking-tight tw-text-slate-900 sm:tw-text-2xl lg:tw-text-[1.75rem] dark:tw-text-white">
              {t('employees.title')}
            </h1>
            <p className="tw-mt-1 tw-hidden tw-text-sm tw-text-slate-500 sm:tw-block [@media(max-height:820px)]:tw-hidden dark:tw-text-slate-400">
              {t('employees.subtitle')}
            </p>
          </div>
        </div>

        {/* Phones: a grid (missing documents on its own row, then add / export /
            leave requests); from sm a single wrapping row in the `order` given */}
        <div className="tw-grid tw-grid-cols-[minmax(0,1fr)_auto_auto] tw-items-center tw-gap-2 sm:tw-flex sm:tw-flex-wrap">
          {missingDocsCount > 0 && (
            <Button
              variant="warning"
              onClick={onOpenMissingDocs}
              title={t('employees.actions.missingDocsHint')}
              className="tw-col-span-3 sm:tw-order-2"
            >
              <LuTriangleAlert aria-hidden="true" className="tw-h-4 tw-w-4" />
              {t('employees.actions.missingDocs')}
              <span className="tw-grid tw-h-5 tw-min-w-[1.25rem] tw-place-items-center tw-rounded-full tw-bg-amber-500 tw-px-1.5 tw-text-[11px] tw-font-bold tw-leading-none tw-text-white tw-tabular-nums">
                {missingDocsCount}
              </span>
            </Button>
          )}

          <Button variant="primary" onClick={onAddEmployee} className="max-sm:tw-w-full sm:tw-order-4">
            <LuUserPlus aria-hidden="true" className="tw-h-4 tw-w-4" />
            {t('employees.actions.addEmployee')}
          </Button>

          <Button
            onClick={onDownload}
            disabled={isExporting}
            aria-label={t('employees.actions.download')}
            title={t('employees.actions.downloadHint')}
            className="max-sm:tw-w-10 max-sm:tw-px-0 sm:tw-order-3"
          >
            {isExporting
              ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin tw-text-emerald-600 dark:tw-text-emerald-400" />
              : <LuFileSpreadsheet aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-emerald-600 dark:tw-text-emerald-400" />}
            <span className="tw-hidden sm:tw-inline">{t('employees.actions.download')}</span>
          </Button>

          <IconButton
            label={t('employees.actions.pendingLeaves', { count: pendingLeaveCount })}
            badge={pendingLeaveCount}
            tone={pendingLeaveCount > 0 ? 'accent' : 'default'}
            onClick={onOpenPendingLeaves}
            className="sm:tw-order-1"
          >
            <LuBell aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
          </IconButton>
        </div>
      </div>
    </section>
  );
};

EmployeePageHeader.propTypes = {
  pendingLeaveCount: PropTypes.number.isRequired,
  missingDocsCount: PropTypes.number.isRequired,
  isExporting: PropTypes.bool,
  onOpenPendingLeaves: PropTypes.func.isRequired,
  onOpenMissingDocs: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  onAddEmployee: PropTypes.func.isRequired,
};

export default EmployeePageHeader;
